import React, {
  createContext, useContext, useEffect, useRef, useState,
} from "react";
import PropTypes from "prop-types";

/**
 * ZY-10356 Phase 1 — Draft preservation for Transcript-mode card forms.
 *
 * Problem the hook solves: forms rendered inside the transcript modal's
 * right-rail (ComplaintForm, DiagnosisForm, MedicineForm, …) hold field
 * values in local useState. When the reviewer switches a section tab OR
 * minimises the modal, React unmounts the form and the typed values are
 * lost. The user has reported this across every card-ful section.
 *
 * Scope: Transcript mode only. Standalone (Orbit) usage of the same
 * form components is unaffected — a form rendered OUTSIDE this provider
 * reads a no-op hook (`null` draft, no-op setter) and continues as
 * today.
 *
 * API shape:
 *   const [draft, saveDraft] = useCardDraft(cardKey);
 *     - `draft` is `null` on first open (form seeds normally), else the
 *       snapshot the form last wrote.
 *     - `saveDraft(snapshot)` writes the snapshot to the parent store,
 *       keyed on `cardKey`.
 *     - Snapshot shape is form-specific (whatever object the form
 *       chooses to serialise). The store treats it as opaque.
 *
 * `cardKey` convention for Phase 1 — the existing cardId string
 * (`m-${ts}` for manual, `u-${recordId}-${ts}` for row-click, etc.).
 * Phase 2 will migrate the key to the server-assigned uniqueKey so the
 * same card survives AI re-emissions across reopen cycles; the hook
 * API stays identical.
 *
 * Lifecycle in CapturedSections:
 *   - Parent wraps the card-render area with <CardDraftProvider>.
 *   - Form mounts: hydrates its own useState from the returned draft.
 *   - Form field changes: form calls saveDraft(fullSnapshot).
 *   - Form unmounts (tab switch, minimise): snapshot stays in the ref.
 *   - Form remounts with the same cardKey: hydrates again from the
 *     preserved snapshot.
 *   - Card committed (Add/Update) or rejected: parent calls
 *     clearDraft(cardKey) so the next open starts clean.
 *   - Transcript modal fully dismisses: provider unmounts, the ref +
 *     every snapshot inside goes with it.
 */

const CardDraftContext = createContext(null);

/**
 * Wrap this around the subtree that renders transcript-mode card
 * forms (CapturedSections' right-rail area). The provider owns ONE ref
 * holding the full `cardKey -> snapshot` map; children subscribe via
 * useCardDraft.
 */
export const CardDraftProvider = ({ children }) => {
  // Ref, not state — the store itself must not re-render the subtree
  // on every keystroke. React re-render for the actual field value
  // happens via the form's own useState (hydrated once at mount).
  const storeRef = useRef(new Map());

  // Bump on clearDraft so consumers that explicitly watch for a reset
  // can react. Not used by the standard form path.
  const [, setBump] = useState(0);

  const value = useRef({
    get: (key) => (key ? storeRef.current.get(key) || null : null),
    set: (key, snapshot) => {
      if (!key) return;
      storeRef.current.set(key, snapshot);
    },
    clear: (key) => {
      if (!key) return;
      if (storeRef.current.delete(key)) setBump((n) => n + 1);
    },
    // Testing / introspection — not part of the stable API.
    _size: () => storeRef.current.size,
  }).current;

  return (
    <CardDraftContext.Provider value={value}>
      {children}
    </CardDraftContext.Provider>
  );
};

CardDraftProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Form-side hook. Returns [draft, saveDraft].
 *
 * Safe to call outside the provider — forms rendered in standalone
 * mode get draft=null + a no-op saveDraft so they behave exactly as
 * they do today.
 *
 * `cardKey` can be null/undefined (e.g. form hasn't been handed an id
 * yet) — both reads and writes no-op in that case.
 */
export const useCardDraft = (cardKey) => {
  const ctx = useContext(CardDraftContext);
  // Read snapshot ONCE at mount (via useState initializer). The form
  // owns the hydrated values in its own state after that — we don't
  // want to re-hydrate on every parent re-render.
  const [draft] = useState(() => (ctx ? ctx.get(cardKey) : null));

  const saveDraft = useRef((snapshot) => {
    if (!ctx) return;
    ctx.set(cardKey, snapshot);
  }).current;

  return [draft, saveDraft];
};

/**
 * Parent-side helper for CapturedSections to clear a draft on commit
 * (Add/Update) or reject. Read via a ref so parent callbacks don't
 * capture a stale closure.
 */
export const useClearCardDraft = () => {
  const ctx = useContext(CardDraftContext);
  const ref = useRef(ctx ? ctx.clear : () => {});
  ref.current = ctx ? ctx.clear : () => {};
  return (key) => ref.current(key);
};
